
<template>
  <div class="modifier-groups">
    <template v-for="group in modifierGroups">
      <cf-modifier-portion-group
        v-if="group.type === 'portion'"
        :key="group.modifierGroupId"
        v-bind="getSharedProps(group)"
        @selected="modifiersSelectedHandler"
      />

      <cf-modifier-select-group
        v-else-if="group.modifierGroupTemplate === 'Dropdown' && !readOnly"
        :key="group.modifierGroupId"
        v-bind="getSharedProps(group)"
        @selected="modifiersSelectedHandler"
      />

      <cf-modifier-group-selection
        v-else
        :key="group.modifierGroupId"
        v-bind="getSharedProps(group)"
        @set-price-override-source-modifer-id="setPriceOverrideSourceModifierId"
        @set-modifier-with-price-override="setModiferWithPriceOverride"
        @selected="modifiersSelectedHandler"
      />
    </template>
  </div>
</template>


<script>
  import CfModifierPortionGroup from './cf-modifier-portion-group/index.vue';
  import CfModifierSelectGroup from './cf-modifier-select-group/index.vue';
  import CfModifierGroupSelection from './cf-modifier-group-selection/index.vue';


  export default {
    name: 'CfModifierGroups',

    components: {
      CfModifierPortionGroup,
      CfModifierSelectGroup,
      CfModifierGroupSelection
    },

    inject: {
      $validator: {
        from: '$validator'
      },

      validatedForm: {
        from: 'validatedForm',
        default() {
          return {};
        }
      }
    },

    props: {
      groups: {
        type: Array,
        required: true
      },

      dynamicGroups: {
        type: Object,
        default() {
          return {};
        }
      },

      hidePrices: {
        type: Boolean,
        default: false
      },

      readOnly: {
        type: Boolean,
        default: false
      },

      /**
       * The modifiers that have been selected for an order item in the cart.
       * This is used to set the modifier selection states when editing an order item.
       */
      selectedModifiers: {
        type: Array,
        default: null
      }
    },

    data() {
      return {
        groupSelections: {},
        modifiersWithPriceOverrides: {},
        priceOverrideSourceModifierId: null,
        dynamicGroupModifierSource: null,
        modifierGroups: [],
        modifierSelections: []
      };
    },

    mounted() {
      this.modifierGroups = this.groups;
    },

    methods: {
      findAndReplaceModifierPrice() {
        // Note: Logic applies to dynamic modifier pricing!
        // For each modifier selection, if is included in modifiersWithPriceOverride array need to replace existing modifier.modifierPriceOverride with new price override.
        // Splice index of modifiers in modifierSelections with priceOverrides whenever this.priceOverrideSourceModifierId changes.
        const modiferSelectionClone = [...this.modifierSelections];

        modiferSelectionClone.forEach((modifier) => {
          const modifierMatch = this.modifiersWithPriceOverrides[modifier.menuItemId];

          if (modifierMatch) {
            const indexOfMod = this.modifierSelections.findIndex(selectedMod => selectedMod.menuItemId === modifierMatch.menuItemId);

            const newPriceOverride = modifierMatch.priceOverrides.find(priceOverride => priceOverride.sourceModifierId === this.priceOverrideSourceModifierId);

            const modifierWithUpdatePriceOverride = {
              ...modifier,
              modifierPriceOverride: newPriceOverride.price,
              sourceModifierId: this.priceOverrideSourceModifierId
            };

            this.modifierSelections.splice(indexOfMod, 1, modifierWithUpdatePriceOverride);
          }
        });
      },

      getSharedProps(group) {
        return {
          hidePrices: this.hidePrices,
          label: group.displayName,
          modifierGroup: group,
          name: `modifier-group-${group.modifierGroupId}`,
          readOnly: this.readOnly,
          selectedModifiers: this.selectedModifiers,
          priceOverrideSourceModifierId: this.priceOverrideSourceModifierId,
          groupSelections: this.groupSelections
        };
      },

      modifiersSelectedHandler(updatedGroup) {
        // Note: Logic applies to dynamic modifier group rendering!
        // If modifierGroup isVirtual, then this modifier group is the top-level modifier group that contains modifiers
        // that, once selected, will dynamically re-render an updated list of selectable modifier groups and items.
        // Ex: If "Size" was the virtual modifier group with modifiers of large, small, and mini, then any of these modifiers could respectively
        // have their own unique modifier group modifiers, like "Bread", "Toppings", "Cheese" etc...
        // So, if large was selected it may have "wheat" and "sourdough" as the "Bread" modifier group's options, while mini when selected may only offer "wheat" as the "Bread" modifier group's option.
        if (updatedGroup.modifierGroup.isVirtual) {
          this.groupSelections = {};

          this.updateDynamicModifierGroups(updatedGroup);
        }

        const existingGroup = this.groupSelections[updatedGroup.modifierGroup.modifierGroupId];

        if (existingGroup) {
          existingGroup.modifiers = updatedGroup.modifiers;
        }

        else {
          this.groupSelections[updatedGroup.modifierGroup.modifierGroupId] = updatedGroup;
        }

        let modifierSelections = [];

        if (this.dynamicGroupModifierSource) {
          this.$emit('set-dynamic-group-modifier-source', this.dynamicGroupModifierSource);
        }

        Object.values(this.groupSelections).forEach((group) => {
          modifierSelections = modifierSelections.concat(group.modifiers);
        });

        this.modifierSelections = modifierSelections;

        if (Object.keys(this.modifiersWithPriceOverrides).length) {
          this.findAndReplaceModifierPrice();
        }

        this.$emit('selected', modifierSelections);
      },

      // Note: Logic applies to dynamic modifier pricing!
      // Source modifier id will determine other modifier pricing within a modifier group once set.
      setPriceOverrideSourceModifierId(sourceModifierId) {
        this.priceOverrideSourceModifierId = sourceModifierId;
      },

      setModiferWithPriceOverride(modifier, isModifierRemoved = false) {
        if (isModifierRemoved) {
          delete this.modifiersWithPriceOverrides[modifier.menuItemId];
        }
        else {
          this.modifiersWithPriceOverrides[modifier.menuItemId] = modifier;
        }
      },

      // Note: Logic applies to dynamic modifier group rendering!
      updateDynamicModifierGroups(updatedGroup) {
        const baseModifierGroup = updatedGroup.modifierGroup;

        const { dynamicGroupModifierSource, dynamicModifierGroup } = this.findMenuItemGroupItemIdMatch(updatedGroup.modifiers);

        if (dynamicGroupModifierSource) {
          this.dynamicGroupModifierSource = dynamicGroupModifierSource;
          // Merge baseModifierGroup with matched menuItemGroupId and dynamicGroupKey to generate new array/list of modiferGroups.
          const newModifierGroups = [baseModifierGroup, ...dynamicModifierGroup];

          // Set modifierGroups to new array/list and re-render selectable modifier groups.
          this.modifierGroups = newModifierGroups;
        }
      },

      // Note: Logic applies to dynamic modifier group rendering!
      findMenuItemGroupItemIdMatch(updatedGroupModifiers) {
        const dynamicGroupKeys = Object.keys(this.dynamicGroups);
        // Find a match within the updateGroupModifiers and object key existing within dynamicGroupKeys
        const dynamicGroupModifierSource = updatedGroupModifiers.find((mod) => {
          // If a selected modifier's menuItemGroupItemId exists within the dynamicGroupKeys return that modifier
          const isIncluded = dynamicGroupKeys.includes(String(mod.menuItemGroupItemId));

          return isIncluded;
        });

        // Extract the dynamicGroups key value using the matched menuItemGroupItemId
        // This extracted value will be an array of objects of modifier groups
        const dynamicModifierGroup = dynamicGroupModifierSource?.menuItemGroupItemId
          ? this.dynamicGroups[dynamicGroupModifierSource.menuItemGroupItemId]
          : {};

        return {
          dynamicGroupModifierSource,
          dynamicModifierGroup
        };
      }
    }
  };
</script>
